import { useState } from "react";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import {
  Camera, Megaphone, TrendingUp, HeartHandshake, ArrowRight, ShieldCheck, Lock, Award,
  CheckCircle2, IndianRupee, Calendar, Star, ChevronRight, Sparkles, Users, ClipboardCheck
} from "lucide-react";
import { toast } from "sonner";

const STEPS = [
  { n: "01", icon: Camera, title: "Property Styling & Brand Identity",
    body: "We rebrand the property — name, identity, photoshoot, video reel and copy. A real launch, not a listing." },
  { n: "02", icon: Megaphone, title: "Social Media & Paid Marketing",
    body: "Daily Reels, paid Meta ads, Google business listing, wedding-planner partnerships — all run by our in-house team." },
  { n: "03", icon: TrendingUp, title: "Lead Generation & Pricing",
    body: "Inbound leads via WhatsApp, calls, our marketplace + dynamic pricing optimised every week to maximise revenue." },
  { n: "04", icon: HeartHandshake, title: "Booking & On-ground Operations",
    body: "Check-ins, vendors, decor, food, music, staff, housekeeping. We run the operation — you only see the revenue." },
];

const STATS = [
  { n: "12+", l: "Managed properties", icon: Award },
  { n: "₹8.4Cr", l: "Revenue delivered '25", icon: IndianRupee },
  { n: "4.92★", l: "Avg. guest rating", icon: Star },
  { n: "2.4×", l: "Revenue uplift / 12mo", icon: TrendingUp },
];

const GUIDELINES = [
  { icon: ShieldCheck, title: "You keep ownership", body: "We never buy, lease, or claim your property. Always 100% yours." },
  { icon: Lock, title: "No lock-in. Ever.", body: "Walk away with 60 days notice. No penalties, no friction, no fine print." },
  { icon: ClipboardCheck, title: "Transparent monthly statements", body: "Every booking, every expense, itemised. Real-time dashboard access." },
];

const COMPARE = [
  { feature: "Photography & branding", us: true, listing_site: false },
  { feature: "Paid marketing & SEO", us: true, listing_site: false },
  { feature: "Dedicated host desk", us: true, listing_site: false },
  { feature: "Onsite staff & vendors", us: true, listing_site: false },
  { feature: "Dynamic pricing optimisation", us: true, listing_site: false },
  { feature: "End-to-end booking ops", us: true, listing_site: false },
  { feature: "Monthly revenue statement", us: true, listing_site: false },
  { feature: "Transparent commission only", us: false, listing_site: true },
];

const FAQ = [
  { q: "How is this different from listing on Airbnb?", a: "Airbnb is a marketplace. Appy Managed is an operating partner. We rebrand, market, sell and operate — all of it. Owners typically see a 2.4× revenue jump in 12 months." },
  { q: "What's the revenue split?", a: "We work on a clean revenue share that depends on the size and condition of your property. We share full numbers in the discovery call. No upfront fees, ever." },
  { q: "How long until my property goes live?", a: "Typically 30 days. That includes shoot, content, listing, channel partnerships and staff onboarding." },
  { q: "Do you buy or lease the property?", a: "No. You keep 100% ownership. We're an operating partner — you can walk away with 60 days notice." },
];

export default function Partner() {
  const [form, setForm] = useState({
    name: "", phone: "", email: "",
    property_name: "", property_location: "Hyderabad",
    property_type: "Farmhouse", bedrooms: 3, notes: "",
  });
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);
  const update = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.phone || !form.property_name) {
      toast.error("Name, phone and property name are required");
      return;
    }
    setSubmitting(true);
    try {
      await api.post("/partners", { ...form, email: form.email || undefined, bedrooms: Number(form.bedrooms) || 1 });
      setDone(true);
      toast.success("Application received — our partnerships team will reach out in 24h.");
    } catch {
      toast.error("Something went wrong. Try again.");
    } finally { setSubmitting(false); }
  };

  return (
    <main data-testid="partner-page" className="bg-[#FAFAF8]">
      {/* HERO — overlapping image + dark panel */}
      <section className="relative">
        <div className="max-w-7xl mx-auto px-6 lg:px-12 pt-12 md:pt-16">
          <div className="relative grid lg:grid-cols-12 gap-0 rounded-[28px] overflow-hidden bg-[#0E2E24] text-[#FAFAF8]">
            {/* image */}
            <div className="lg:col-span-7 relative aspect-[4/3] lg:aspect-auto lg:h-[560px]">
              <img src="https://images.unsplash.com/photo-1613977257363-707ba9348227?auto=format&fit=crop&q=85&w=1600" alt="" className="img-cover" />
              <div className="absolute inset-0 bg-[linear-gradient(90deg,transparent_55%,rgba(14,46,36,0.6)_100%)] hidden lg:block" />
              {/* Floating mini-card top-left */}
              <div className="hidden lg:flex items-center gap-3 absolute top-6 left-6 bg-[#FAFAF8] rounded-2xl px-4 py-3 shadow-lg">
                <Sparkles size={16} className="text-[#F4A930]" />
                <div className="text-xs font-sora">
                  <div className="font-semibold text-[#0E2E24]">Olive Estate</div>
                  <div className="text-[#5B6B63]">+318% revenue · 9 months</div>
                </div>
              </div>
              {/* Floating mini-card bottom-right */}
              <div className="hidden lg:block absolute bottom-6 right-6 bg-[#F4A930] text-[#0E2E24] rounded-2xl px-5 py-3 shadow-lg">
                <div className="text-[10px] tracking-widest uppercase font-sora font-bold">Live in</div>
                <div className="font-sora font-semibold text-2xl leading-none mt-0.5">30 days</div>
              </div>
            </div>
            {/* copy */}
            <div className="lg:col-span-5 p-8 md:p-12 lg:p-14 flex flex-col justify-center">
              <div className="kicker text-[#F4A930] mb-4">Appy Managed · For owners</div>
              <h1 className="font-sora text-3xl md:text-4xl lg:text-[2.8rem] font-semibold tracking-tight leading-[1.05]">
                Turn your farmhouse into a <span className="text-[#7CB342]">revenue engine.</span>
              </h1>
              <p className="mt-5 text-[#FAFAF8]/85 leading-relaxed">
                You own a beautiful property. We handle brand, bookings, operations and guests — end-to-end. You earn, without lifting a finger.
              </p>
              <div className="mt-7 flex flex-wrap gap-3">
                <a href="#apply" className="inline-flex items-center gap-2 rounded-full bg-[#F4A930] hover:bg-[#d99825] text-[#0E2E24] font-sora font-semibold px-6 py-3.5 transition-colors" data-testid="partner-apply-hero">
                  Apply to partner <ArrowRight size={16} />
                </a>
                <a href="#how-it-works" className="inline-flex items-center gap-2 rounded-full border border-[#FAFAF8]/30 bg-transparent hover:bg-[#FAFAF8]/10 text-[#FAFAF8] font-sora font-medium px-6 py-3.5 transition-colors">
                  See how it works
                </a>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* STATS — minimal premium row */}
      <section className="max-w-7xl mx-auto px-6 lg:px-12 mt-10">
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3 md:gap-4">
          {STATS.map((s) => (
            <div key={s.l} className="bg-white border border-[#0E2E24]/10 rounded-2xl p-6 flex items-start gap-4">
              <div className="h-11 w-11 rounded-xl bg-[#E8F2EC] flex items-center justify-center shrink-0">
                <s.icon size={18} className="text-[#1F5A43]" />
              </div>
              <div>
                <div className="font-sora font-semibold text-2xl text-[#0E2E24] leading-none">{s.n}</div>
                <div className="mt-1 text-xs tracking-[0.18em] uppercase font-sora font-semibold text-[#1F5A43]">{s.l}</div>
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* PROCESS — 4-step visual flow */}
      <section id="how-it-works" className="max-w-7xl mx-auto px-6 lg:px-12 py-24 md:py-28">
        <div className="kicker mb-4">How we operate</div>
        <h2 className="font-sora text-3xl md:text-5xl font-semibold tracking-tight text-[#0E2E24] max-w-3xl leading-tight">
          Four moves. Zero operations on your plate.
        </h2>

        <div className="mt-12 relative">
          {/* connector line */}
          <div className="hidden lg:block absolute left-0 right-0 top-[44px] h-px bg-gradient-to-r from-transparent via-[#0E2E24]/15 to-transparent" />
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-5">
            {STEPS.map((s, i) => (
              <div key={s.n} className="relative bg-white border border-[#0E2E24]/10 rounded-2xl p-7 hover-lift" data-testid={`partner-step-${i + 1}`}>
                {/* step number circle on top of connector */}
                <div className="hidden lg:flex absolute -top-5 left-7 h-10 w-10 rounded-full bg-[#0E2E24] text-[#F4A930] items-center justify-center font-sora font-semibold text-sm border-4 border-[#FAFAF8]">
                  {i + 1}
                </div>
                <div className="lg:mt-2 flex items-start justify-between">
                  <div className="h-12 w-12 rounded-xl bg-[#E8F2EC] flex items-center justify-center">
                    <s.icon size={20} className="text-[#1F5A43]" />
                  </div>
                  <span className="font-sora font-semibold text-lg text-[#F4A930]/70 lg:hidden">{s.n}</span>
                </div>
                <h3 className="mt-6 font-sora text-lg font-semibold text-[#0E2E24] leading-tight">{s.title}</h3>
                <p className="mt-3 text-sm text-[#5B6B63] leading-relaxed">{s.body}</p>
                {i < STEPS.length - 1 && (
                  <ChevronRight size={18} className="hidden lg:block absolute -right-3.5 top-12 text-[#0E2E24]/30" />
                )}
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* COMPARISON TABLE */}
      <section className="bg-white border-y border-[#0E2E24]/10 py-24 md:py-28">
        <div className="max-w-5xl mx-auto px-6 lg:px-12">
          <div className="kicker mb-4">Listing vs Operating</div>
          <h2 className="font-sora text-3xl md:text-4xl font-semibold tracking-tight text-[#0E2E24] max-w-2xl leading-tight">
            Why owners pick Appy Managed over a generic marketplace.
          </h2>
          <div className="mt-10 rounded-2xl border border-[#0E2E24]/10 overflow-hidden">
            <div className="grid grid-cols-12 bg-[#0E2E24] text-[#FAFAF8] font-sora text-sm font-semibold">
              <div className="col-span-6 p-4 px-6">Feature</div>
              <div className="col-span-3 p-4 text-center">Appy Managed</div>
              <div className="col-span-3 p-4 text-center text-[#FAFAF8]/70">Listing site</div>
            </div>
            {COMPARE.map((row, i) => (
              <div key={row.feature} className={`grid grid-cols-12 ${i % 2 === 0 ? "bg-[#FAFAF8]" : "bg-white"}`}>
                <div className="col-span-6 p-4 px-6 font-sora text-sm text-[#0E2E24]">{row.feature}</div>
                <div className="col-span-3 p-4 text-center">{row.us ? <CheckCircle2 size={18} className="mx-auto text-[#7CB342]" /> : <span className="text-[#5B6B63]/50">—</span>}</div>
                <div className="col-span-3 p-4 text-center">{row.listing_site ? <CheckCircle2 size={18} className="mx-auto text-[#5B6B63]" /> : <span className="text-[#5B6B63]/50">—</span>}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* GUIDELINES */}
      <section className="bg-[#E8F2EC] py-20">
        <div className="max-w-7xl mx-auto px-6 lg:px-12">
          <div className="kicker mb-3">Our promise</div>
          <h2 className="font-sora text-3xl md:text-4xl font-semibold tracking-tight text-[#0E2E24] max-w-3xl leading-tight">
            The fine print, in three sentences.
          </h2>
          <div className="mt-12 grid md:grid-cols-3 gap-5">
            {GUIDELINES.map((g) => (
              <div key={g.title} className="bg-white border border-[#0E2E24]/10 rounded-2xl p-7">
                <div className="h-11 w-11 rounded-xl bg-[#F4A930]/20 flex items-center justify-center">
                  <g.icon size={18} className="text-[#1F5A43]" />
                </div>
                <h3 className="mt-5 font-sora text-lg font-semibold text-[#0E2E24]">{g.title}</h3>
                <p className="mt-2 text-sm text-[#5B6B63] leading-relaxed">{g.body}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* TESTIMONIAL — owner voice */}
      <section className="max-w-5xl mx-auto px-6 lg:px-12 py-24 md:py-28">
        <div className="bg-[#0E2E24] text-[#FAFAF8] rounded-[28px] p-10 md:p-14 grid md:grid-cols-12 gap-8 items-center">
          <div className="md:col-span-3">
            <div className="rounded-2xl overflow-hidden bg-[#E8F2EC]">
              <img src="https://api.dicebear.com/9.x/lorelei/svg?seed=VikramIyer" alt="" className="img-cover" />
            </div>
          </div>
          <div className="md:col-span-9">
            <div className="kicker text-[#F4A930] mb-4">Owner story</div>
            <blockquote className="font-sora text-xl md:text-2xl font-semibold leading-snug">
              "My farmhouse was idle for two years. Appy Stays rebuilt the brand, ran the social, brought guests, ran the kitchen. I now get a real monthly cheque — and I never go on-site."
            </blockquote>
            <div className="mt-5 font-sora text-sm">
              <div className="font-semibold">Vikram Iyer</div>
              <div className="text-[#FAFAF8]/65">Owner, Olive Estate · Moinabad</div>
            </div>
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section className="max-w-5xl mx-auto px-6 lg:px-12 pb-20">
        <div className="kicker mb-4">FAQ</div>
        <h2 className="font-sora text-3xl md:text-4xl font-semibold tracking-tight text-[#0E2E24] leading-tight max-w-2xl">
          Questions owners ask us first.
        </h2>
        <div className="mt-10 grid md:grid-cols-2 gap-4">
          {FAQ.map((f) => (
            <div key={f.q} className="bg-white border border-[#0E2E24]/10 rounded-2xl p-6">
              <h3 className="font-sora text-base font-semibold text-[#0E2E24]">{f.q}</h3>
              <p className="mt-2 text-sm text-[#5B6B63] leading-relaxed">{f.a}</p>
            </div>
          ))}
        </div>
      </section>

      {/* APPLICATION */}
      <section id="apply" className="max-w-7xl mx-auto px-6 lg:px-12 pb-24 md:pb-32">
        <div className="grid lg:grid-cols-12 gap-12 bg-white border border-[#0E2E24]/10 rounded-[28px] p-8 md:p-12">
          <div className="lg:col-span-5">
            <div className="kicker mb-4">Partnership application</div>
            <h2 className="font-sora text-3xl md:text-4xl font-semibold tracking-tight text-[#0E2E24] leading-[1.05]">
              Tell us about your property.
            </h2>
            <p className="mt-5 text-[#5B6B63] leading-relaxed max-w-md">
              We review every application personally. If it's a fit, our partnerships lead will call within 24 hours.
            </p>
            <ul className="mt-8 space-y-3 text-sm font-sora text-[#0E2E24]/85">
              {[
                ["Calendar size", "30 days to first booking"],
                ["Setup fees", "Zero"],
                ["Lock-in", "None"],
                ["You retain", "100% ownership"],
              ].map(([k, v]) => (
                <li key={k} className="flex items-baseline justify-between border-b border-[#0E2E24]/8 pb-2">
                  <span className="text-[#5B6B63]">{k}</span>
                  <span className="font-semibold text-[#0E2E24]">{v}</span>
                </li>
              ))}
            </ul>
            <div className="mt-8 flex items-center gap-3 text-sm text-[#5B6B63]">
              <Users size={15} className="text-[#7CB342]" />
              We've already partnered with 12+ farmhouse owners across Hyderabad.
            </div>
          </div>

          <div className="lg:col-span-7">
            {done ? (
              <div className="bg-[#E8F2EC] border border-[#0E2E24]/10 rounded-2xl p-10 md:p-14 text-center">
                <div className="h-14 w-14 rounded-full bg-white border-2 border-[#7CB342] flex items-center justify-center mx-auto">
                  <CheckCircle2 size={26} className="text-[#7CB342]" />
                </div>
                <h3 className="mt-6 font-sora text-2xl font-semibold text-[#0E2E24]">Thank you — we've got it.</h3>
                <p className="mt-3 text-[#5B6B63] max-w-md mx-auto">
                  We'll review and call <span className="font-semibold text-[#0E2E24]">{form.phone}</span> within 24 hours.
                </p>
                <Link to="/" className="mt-7 inline-flex text-[#1F5A43] font-sora font-medium hover:text-[#0E2E24] underline-offset-4 hover:underline">
                  ← Back to homepage
                </Link>
              </div>
            ) : (
              <form onSubmit={submit} className="space-y-5" data-testid="partner-form">
                <div className="grid md:grid-cols-2 gap-4">
                  <Field label="Your name" required>
                    <Input value={form.name} onChange={(e) => update("name", e.target.value)} data-testid="partner-name" />
                  </Field>
                  <Field label="Phone" required>
                    <Input value={form.phone} onChange={(e) => update("phone", e.target.value)} data-testid="partner-phone" />
                  </Field>
                </div>
                <Field label="Email">
                  <Input type="email" value={form.email} onChange={(e) => update("email", e.target.value)} data-testid="partner-email" />
                </Field>
                <div className="grid md:grid-cols-2 gap-4">
                  <Field label="Property name" required>
                    <Input value={form.property_name} onChange={(e) => update("property_name", e.target.value)} placeholder="e.g. Lotus House" data-testid="partner-property-name" />
                  </Field>
                  <Field label="Location">
                    <Input value={form.property_location} onChange={(e) => update("property_location", e.target.value)} data-testid="partner-location" />
                  </Field>
                </div>
                <div className="grid md:grid-cols-2 gap-4">
                  <Field label="Property type">
                    <Select value={form.property_type} onValueChange={(v) => update("property_type", v)}>
                      <SelectTrigger data-testid="partner-type"><SelectValue /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="Farmhouse">Farmhouse</SelectItem>
                        <SelectItem value="Villa">Villa</SelectItem>
                        <SelectItem value="Bungalow">Bungalow</SelectItem>
                        <SelectItem value="Resort">Boutique Resort</SelectItem>
                      </SelectContent>
                    </Select>
                  </Field>
                  <Field label="Bedrooms">
                    <Input type="number" min={1} value={form.bedrooms} onChange={(e) => update("bedrooms", e.target.value)} data-testid="partner-bedrooms" />
                  </Field>
                </div>
                <Field label="Tell us more about the property">
                  <Textarea rows={4} value={form.notes} onChange={(e) => update("notes", e.target.value)} placeholder="Size, amenities, current state, any links or images…" data-testid="partner-notes" />
                </Field>

                <Button type="submit" disabled={submitting} className="w-full bg-[#0E2E24] hover:bg-[#1F5A43] text-[#FAFAF8] rounded-full py-6 font-sora font-medium gap-2" data-testid="partner-submit">
                  {submitting ? "Sending…" : <>Submit application <ArrowRight size={16} /></>}
                </Button>
                <p className="text-xs text-[#5B6B63] text-center">
                  By submitting, you agree to let Appy Stays contact you about this property.
                </p>
              </form>
            )}
          </div>
        </div>
      </section>
    </main>
  );
}

function Field({ label, required, children }) {
  return (
    <div>
      <Label className="text-xs tracking-[0.18em] uppercase text-[#1F5A43] font-semibold mb-1.5 block">
        {label} {required && <span className="text-[#F4A930]">*</span>}
      </Label>
      {children}
    </div>
  );
}
